import React, { useState } from 'react';
import { Calendar, Users, DollarSign, TrendingUp, ArrowRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import { fetchBookings } from '../../lib/bookingsApi';
import BookingDetailModal from '../bookings/BookingDetailModal';
import NewBookingModal from '../bookings/NewBookingModal';

const STATUS_COLOR = {
  confirmed: 'bg-emerald-100 text-emerald-700',
  pending:   'bg-amber-100 text-amber-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  no_show:  'bg-slate-100 text-slate-600',
};

function StatCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="card p-3 sm:p-5 flex items-start gap-3 sm:gap-4">
      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-ink-muted font-medium uppercase tracking-wide truncate">{label}</p>
        <p className="text-xl sm:text-2xl font-semibold text-ink mt-0.5">{value}</p>
        {sub && <p className="text-xs text-ink-secondary mt-0.5 truncate">{sub}</p>}
      </div>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

export default function Dashboard() {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew]   = useState(false);

  const todayStart = dayjs().startOf('day').toISOString();
  const todayEnd   = dayjs().endOf('day').toISOString();
  const weekEnd    = dayjs().endOf('week').toISOString();

  const { data: todayData, refetch } = useQuery({
    queryKey: ['bookings-today'],
    queryFn: () => fetchBookings({ from: todayStart, to: todayEnd, status: 'confirmed,pending', limit: 50 }),
  });

  const { data: upcomingData } = useQuery({
    queryKey: ['bookings-upcoming'],
    queryFn: () => fetchBookings({ from: todayStart, to: weekEnd, status: 'confirmed,pending', limit: 8 }),
  });

  const todayBookings    = todayData?.data || [];
  const upcomingBookings = upcomingData?.data || [];

  const todayRevenue = todayBookings
    .filter(b => b.status === 'confirmed' || b.status === 'completed')
    .reduce((s, b) => s + (b.Service?.price_cents || 0), 0);

  return (
    <div className="p-4 sm:p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-ink">
          Good {getGreeting()}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-ink-secondary text-sm mt-0.5">Here's what's happening at {tenant?.name} today.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard label="Today's bookings" value={todayBookings.length}                         sub={`${todayBookings.filter(b => b.status === 'confirmed').length} confirmed`} icon={Calendar}   color="bg-primary" />
        <StatCard label="This week"         value={upcomingBookings.length}                     sub="upcoming"                 icon={TrendingUp} color="bg-violet-500" />
        <StatCard label="Today's revenue"   value={`$${(todayRevenue / 100).toFixed(0)}`}       sub="confirmed bookings"       icon={DollarSign} color="bg-emerald-500" />
        <StatCard label="Active clients"    value="—"                                           sub="Fetched in Phase 8"       icon={Users}      color="bg-amber-500" />
      </div>

      {/* Quick actions */}
      <div className="card p-5 mb-6">
        <h2 className="text-sm font-semibold text-ink mb-4">Quick actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'New booking',  action: () => setShowNew(true) },
            { label: 'View calendar', action: () => navigate('/calendar') },
            { label: 'Add service',  action: () => navigate('/services') },
            { label: 'Embed widget', action: () => navigate('/embed') },
          ].map(a => (
            <button key={a.label} onClick={a.action}
              className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-surface-border hover:border-primary/40 hover:bg-primary/5 text-sm font-medium text-ink transition-all group text-left">
              {a.label}
              <ArrowRight size={14} className="text-ink-muted group-hover:text-primary transition-colors" />
            </button>
          ))}
        </div>
      </div>

      {/* Today's schedule */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-surface-border">
          <h2 className="text-sm font-semibold text-ink">Today's schedule</h2>
          <button onClick={() => navigate('/calendar')} className="text-xs text-primary hover:underline">View calendar</button>
        </div>
        {!todayBookings.length ? (
          <div className="py-10 text-center text-ink-muted text-sm">No appointments today.</div>
        ) : (
          <ul className="divide-y divide-surface-border overflow-x-auto">
            {todayBookings.map(b => (
              <li
                key={b.id}
                onClick={() => setSelected(b)}
                className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 hover:bg-surface-50 cursor-pointer transition-colors min-w-0"
              >
                <div className="text-center w-12 shrink-0">
                  <p className="text-sm font-semibold text-ink">{dayjs(b.start_at).format('h:mm')}</p>
                  <p className="text-xs text-ink-muted">{dayjs(b.start_at).format('A')}</p>
                </div>
                {b.Staff?.color && (
                  <div className="w-1 h-10 rounded-full shrink-0" style={{ background: b.Staff.color }} />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">{b.Client?.name}</p>
                  <p className="text-xs text-ink-muted truncate">{b.Service?.name} · {b.Staff?.name}</p>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${STATUS_COLOR[b.status] || ''}`}>
                  {b.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <BookingDetailModal
          booking={selected}
          onClose={() => setSelected(null)}
          onRefresh={refetch}
        />
      )}
      {showNew && (
        <NewBookingModal onClose={() => { setShowNew(false); refetch(); }} />
      )}
    </div>
  );
}
